import React from "react";
import { Layout, Spin, Descriptions, Typography, Table, Button, Flex } from "antd";
import { useParams } from "react-router-dom";
import { FilePdfOutlined } from "@ant-design/icons";
import HeaderScreen from "../common/HeaderScreen";
import { SECONDARY_COLOR, WHITE_COLOR } from "../constants/color";
import { GET } from "../frameworks/HttpClient";
import { URL_REPORT } from "../constants/urls";
import ErrorMessages from "../common/ErrorMessages";
import { dateTimeToHumanString, formatComma } from "../frameworks/Util";
import { POND_STATUS_TH, TANK_STATUS_TH } from "../constants/string";
import ReportButton from "../components/common/ReportButton";
import { PATH } from "../routes/CustomRoutes";
import _ from "lodash";

export default function PondSummaryScreen() {
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    if(id){
      fetchData();
    }
  },[id])

  const fetchData = async() => {
    setLoading(true);
    setErrorMessages(null);
    try {
      const response = await GET(`${URL_REPORT.POND_SUMMARY}${id}/`);
      setData(response.data);
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <HeaderScreen 
        title="รายละเอียดสต๊อกน้ำยางสด" 
        style={{ backgroundColor : SECONDARY_COLOR, 
        color : WHITE_COLOR, fontSize : 18, fontWeight: 'bolder' }}
        onClick={() => window.close()}
      />  
      <Layout.Content
        style={{ margin: 24, minHeight: 280, padding: "0px 24px", }}
      >
        <Spin spinning={loading}>
          <ErrorMessages message={errorMessages}/>
          <Descriptions
            size='small'
            title={
              <Flex justify="space-between">
                ข้อมูลเบื้องต้น
                <ReportButton url={`${URL_REPORT.POND_SUMMARY_REPORT}${id}/`}>พิมพ์รายงาน</ReportButton>
              </Flex>
            }
            bordered
            column={1}
            items={[
              { label: 'หมายเลขบ่อ', children: data?.pond_name },
              { label: 'ล็อต', children: data?.lot_no },
              { label: 'วันที่เริ่มรับซื้อ', children: dateTimeToHumanString(data?.created) },
              { label: 'วันที่สิ้นสุดรับซื้อ', children: data?.close_datetime ? dateTimeToHumanString(data?.close_datetime) : '-' },
              { label: 'ค่าที่วัดได้', children: formatComma(data?.current_height) },
              { label: 'น้ำหนักที่รับซื้อ', children: formatComma(data?.truck_transactions_weight) },
              { label: 'สถานะ', children: POND_STATUS_TH[data?.state] },
              { label: 'ผู้ทำรายการเริ่มต้น', children: data?.created_by_name },
            ]}
          />
          <Typography.Title level={5}>รายละเอียดผู้ขาย</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ชื่อผู้ขาย', render: (record) => `${record?.supplier_code} - ${record?.supplier_name}` },
              { title: 'ทะเบียนรถ', dataIndex: 'license_plate' },
              { title: 'วันที่ขาย', dataIndex: 'in_datetime', render: (value) => dateTimeToHumanString(value) },
              { 
                title: 'น้ำหนักยางสด', 
                dataIndex: 'net_weight', 
                render: (value) => <div style={{textAlign: 'right'}}>{formatComma(value)}</div> 
              },
              { 
                title: 'น้ำหนัก DRC', 
                dataIndex: 'dry_weight', 
                render: (value) => <div style={{textAlign: 'right'}}>{formatComma(value) }</div>
              },
              { title: '%NH3', dataIndex: 'nh3', render: (value) => value.toFixed(2) },
              { title: 'VFA No', dataIndex: 'vfa', render: (value) => value.toFixed(3) },
              { title: 'TZ (kg/T)', dataIndex: 'tz', render: (value) => value.toFixed(2) },
            ]}
            dataSource={data?.truck_transactions}
            summary={pageData => {
              let total_net_weight = 0;
              let total_dry_weight = 0;
              pageData.forEach(({ net_weight, dry_weight }) => {
                total_net_weight += Number(net_weight);
                total_dry_weight += Number(dry_weight);
              });
              return (
                <>{_.get(data, 'truck_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}/>
                    <Table.Summary.Cell index={3}>
                      <div style={{textAlign: 'right'}}>{formatComma(total_net_weight)}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      <div style={{textAlign: 'right'}}>{formatComma(total_dry_weight)}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5}/>
                    <Table.Summary.Cell index={6}/>
                    <Table.Summary.Cell index={7}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>รายการใช้สารเคมีคุณภาพ</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อตเคมีคุณภาพ', dataIndex: 'lot_no', render: (value, original) => <span> {value} {value && <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.QUALITY_CHEMICAL_SUMMARY}/${original.stock_ref}`)} />} </span> },
              { title: 'วันที่สั่ง', dataIndex: 'order_date', render: (value) => dateTimeToHumanString(value) },
              { 
                title: 'ปริมาณสั่ง', 
                dataIndex: 'amount', 
                render: (value) => <div style={{textAlign: 'right'}}>{formatComma(value)}</div>
              },
              { title: 'ผู้สั่งงาน', dataIndex: 'lab_user' },
              { title: 'ผู้รับงาน', dataIndex: 'input_user' },
            ]}
            dataSource={data?.lab_inputs}
            summary={pageData => {
              let total_amount = 0;
              pageData.forEach(({ amount }) => {
                total_amount += Number(amount);
              });
              return (
                <>{_.get(data, 'lab_inputs.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}>
                      <div style={{textAlign: 'right'}}>{formatComma(total_amount)}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
          <Typography.Title level={5}>รายการผลิตน้ำยางข้น</Typography.Title>
          <Table
            size='small'
            bordered
            pagination={false}
            columns={[
              { title: 'ล็อต', dataIndex: 'lot_no', render: (value, original) => <span>{value} <Button type="link" icon={<FilePdfOutlined />} onClick={() => window.open(`${window.origin}${PATH.TANK_SUMMARY}/${original.id}`)} /></span> },
              { title: 'แทงก์', dataIndex: 'tank_name' },
              { title: 'วันที่ผลิต', dataIndex: 'created', render: (value) => dateTimeToHumanString(value) },
              { 
                title: 'ปริมาณสั่ง', 
                dataIndex: 'raw_latex_weight', 
                render: (value) => <div style={{textAlign: 'right'}}>{formatComma(value)}</div> 
              },
              { title: 'สถานะล็อต', dataIndex: 'state', render: (value) => TANK_STATUS_TH[value] },
            ]}
            dataSource={data?.tank_transactions}
            summary={pageData => {
              let total_raw_latex_weight = 0;
              pageData.forEach(({ raw_latex_weight }) => {
                total_raw_latex_weight += Number(raw_latex_weight);
              });
              return (
                <>{_.get(data, 'tank_transactions.length', 0) > 0 &&
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>รวม</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}/>
                    <Table.Summary.Cell index={2}/>
                    <Table.Summary.Cell index={3}>
                      <div style={{textAlign: 'right'}}>{formatComma(total_raw_latex_weight)}</div>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}/>
                  </Table.Summary.Row>}
                </>
              );
            }}/>
        </Spin>
      </Layout.Content>
    </Layout>
  )
}